import { useEffect } from 'react';

type Theme = 'light' | 'dark' | 'system';

/**
 * Hook to initialize the theme system
 * This ensures the correct theme is applied immediately on app load
 * before any components render, preventing flash of wrong theme
 */
export const useThemeInitialization = () => {
  useEffect(() => {
    // Get saved theme preference
    const savedTheme = localStorage.getItem('sift-hub-theme') as Theme || 'system';
    
    // Apply theme immediately
    const root = document.documentElement;
    
    if (savedTheme === 'system') {
      // Remove explicit theme to let CSS media queries handle it
      root.removeAttribute('data-theme');
    } else {
      // Set explicit theme
      root.setAttribute('data-theme', savedTheme);
    }
  }, []);
};

/**
 * Utility function to get the current effective theme
 */
export const getCurrentTheme = (): 'light' | 'dark' => {
  const savedTheme = localStorage.getItem('sift-hub-theme') as Theme || 'system';
  
  if (savedTheme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  
  return savedTheme as 'light' | 'dark';
};

/**
 * Utility function to set theme programmatically
 */
export const setTheme = (theme: Theme) => {
  const root = document.documentElement;
  
  if (theme === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
  
  localStorage.setItem('sift-hub-theme', theme);
};